import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Sparkles, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { PLAN_NAMES, longDate } from "@/lib/format";

const BENEFITS = ["PDF, Word, PowerPoint, planilhas e vídeos", "Pasta Segura com todos os tipos de arquivo", "Até 5 GB de armazenamento", "IA completa e busca avançada", "Busca dentro de arquivos e resumos por IA", "Exportação completa e backup ampliado"];
const TIER_TEXT = {
  plus: { title: "Clicou, Guardou! Premium", tagline: "Guarde muito mais.", desc: "Documentos, planilhas, apresentações, vídeos, Pasta Segura completa, mais espaço e IA completa." },
  familia: { title: "Clicou, Guardou! Família", tagline: "Guarde muito mais, juntos.", desc: "Tudo do Premium para até 5 membros, com área compartilhada." },
};

export default function Paywall() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [tier, setTier] = useState("plus");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(!!params.get("session_id"));
  useEffect(() => { api.get("/users/plans").then((r) => setPlans(r.data)); }, []);

  useEffect(() => {
    if (params.get("checkout") === "cancel") { toast("Pagamento não concluído. Você continua no plano Free."); setParams({}, { replace: true }); }
  }, [params, setParams]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0; let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setUser(data.user); setConfirming(false); setParams({}, { replace: true }); toast.success("Pagamento confirmado! Você agora é Premium."); return; }
        if (["expired", "failed"].includes(data.payment_status) || tries++ >= 10) { setConfirming(false); setParams({}, { replace: true }); toast.error("Não conseguimos confirmar o pagamento ainda. Se você pagou, o Premium será ativado em instantes."); return; }
        setTimeout(poll, 2000);
      } catch (e) { if (!cancelled) { setConfirming(false); toast.error(errorMessage(e)); } }
    };
    poll();
    return () => { cancelled = true; };
  }, [params, setParams, setUser]);

  const subscribe = async (billing) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { billing, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };
  const cancelSub = async () => {
    if (!window.confirm("Cancelar a assinatura? Você continua Premium até o fim do período já pago e depois volta ao plano Free.")) return;
    setBusy(true);
    try { const { data } = await api.post("/billing/cancel"); setUser(data.user); toast.success("Cancelamento agendado. Seu Premium continua até o fim do período pago."); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const resumeSub = async () => {
    setBusy(true);
    try { const { data } = await api.post("/billing/resume"); setUser(data.user); toast.success("Assinatura reativada."); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };

  useEffect(() => {
    const p = params.get("pagamento");
    if (p === "atualizado") { toast.success("Forma de pagamento atualizada."); setParams({}, { replace: true }); }
    else if (p === "atualizar" && user?.subscription?.stripe_customer_id) { setParams({}, { replace: true }); openPortal(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, user?.subscription?.stripe_customer_id]);

  const fmt = (v) => v?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const sub = user?.subscription || {};
  const isPremium = user?.plan !== "free";

  return (
    <div className="pb-10">
      <PageHeader title="Assinatura" />
      <div className="ga-fade-up px-5">
        <div className="rounded-3xl p-6 text-white" style={{ background: "linear-gradient(135deg,#05324A,#0A6373 70%,#13C9A3)" }}>
          <Sparkles color="#33D9B2" />
          <h2 className="mt-3 text-3xl font-semibold leading-tight">Guarde ainda mais.</h2>
          <p className="mt-1 text-sm text-white/80" data-testid="paywall-tier-desc">{TIER_TEXT[tier].desc}</p>
        </div>

        <div className="ga-card mt-4 p-4 text-sm" data-testid="paywall-free-desc">
          <div className="font-semibold">Clicou, Guardou! Free</div>
          <div className="ga-muted text-xs">Para guardar o essencial do dia a dia. Textos, fotos, áudios, lembretes ilimitados e 1 Pasta Segura. Até 500 MB.</div>
        </div>

        <div className="ga-surface mt-4 flex rounded-2xl p-1">
          {[["plus", "Premium"], ["familia", "Família"]].map(([k, l]) => (
            <button key={k} onClick={() => setTier(k)} className="flex-1 rounded-xl py-2.5 text-sm font-medium" style={tier === k ? { background: "var(--ga-card)", color: "#0A6373" } : { color: "var(--ga-muted)" }} data-testid={`paywall-tier-${k}`}>{l}</button>
          ))}
        </div>

        <ul className="ga-card mt-4 space-y-3 p-5" data-testid="paywall-benefits">
          <li className="text-sm"><div className="font-semibold">{TIER_TEXT[tier].title}</div><div className="ga-muted text-xs">{TIER_TEXT[tier].tagline}</div></li>
          {(tier === "plus" ? BENEFITS : ["Tudo do Clicou, Guardou! Premium", "Até 5 membros", "Área compartilhada", "Memórias compartilhadas", "Lembretes compartilhados"]).map((b) => (
            <li key={b} className="flex items-center gap-3 text-sm"><span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ background: "#13C9A322", color: "#13C9A3" }}><Check size={14} /></span>{b}</li>
          ))}
          {tier === "familia" && <li className="ga-muted text-xs">Cada membro mantém sua área privada. Nada é compartilhado sem sua escolha.</li>}
        </ul>

        {confirming && <div className="ga-card mt-5 p-5 text-center text-sm" data-testid="paywall-confirming">Confirmando seu pagamento com o Stripe...</div>}

        {tier === "familia" && !confirming && (
          <div className="ga-card mt-5 p-5 text-center" data-testid="paywall-familia-soon">
            <p className="text-sm font-semibold">Em breve</p>
            <p className="ga-muted mt-1 text-xs">O plano Família ainda não está disponível para assinatura.</p>
          </div>
        )}

        {plans && !confirming && tier === "plus" && !isPremium && (
          <div className="mt-5 space-y-3">
            <button onClick={() => subscribe("monthly")} disabled={busy} className="ga-btn-primary w-full justify-between px-6 text-base" data-testid="paywall-upgrade-button"><span>ASSINAR MENSAL</span><span>{fmt(plans.price_monthly)}/mês</span></button>
            <button onClick={() => subscribe("yearly")} disabled={busy} className="ga-btn-dark w-full justify-between px-6 text-base" data-testid="paywall-yearly-button"><span>ASSINAR ANUAL</span><span>{fmt(plans.price_yearly)}/ano</span></button>
            <button onClick={() => navigate(-1)} className="ga-muted w-full py-2 text-sm font-medium" data-testid="paywall-continue-free">CONTINUAR GRÁTIS</button>
            <p className="ga-muted text-center text-[11px]">Pagamento seguro processado pelo Stripe. Cancele quando quiser.</p>
          </div>
        )}
        {isPremium && !confirming && tier === "plus" && (
          <div className="ga-card mt-5 p-5 text-center" data-testid="paywall-current-plan">
            <p className="text-sm">Seu plano atual: <strong>{PLAN_NAMES[user.plan]}</strong></p>
            {sub.status === "past_due" && <p className="mt-2 text-xs font-medium" style={{ color: "#B45309" }} data-testid="paywall-past-due">Não conseguimos processar seu último pagamento. Atualize o cartão para manter o Premium.</p>}
            {sub.current_period_end && <p className="ga-muted mt-2 text-xs" data-testid="paywall-period-end">{sub.cancel_at_period_end ? "Premium ativo até" : "Próxima renovação em"} {longDate(sub.current_period_end)}</p>}
            {sub.stripe_customer_id && sub.status && (
              <button onClick={openPortal} disabled={busy} className={`${sub.status === "past_due" ? "ga-btn-primary" : "ga-btn-ghost"} mt-3 w-full`} data-testid="paywall-update-payment"><CreditCard size={18} /> Atualizar forma de pagamento</button>
            )}
            {sub.cancel_at_period_end
              ? <button onClick={resumeSub} disabled={busy} className="ga-btn-primary mt-3 w-full" data-testid="paywall-resume">Manter minha assinatura</button>
              : sub.status && <button onClick={cancelSub} disabled={busy} className="ga-muted mt-3 text-xs underline" data-testid="paywall-cancel">Cancelar assinatura</button>}
          </div>
        )}
      </div>
    </div>
  );
}
